import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardHeader,
  Typography,
  CircularProgress,
  Box,
  Divider,
  useMediaQuery,
  useTheme
} from '@mui/material';
// Import FieldFactory directly instead of from index to avoid circular dependency
import FieldFactory from './FieldFactory';
import * as fieldCategoryUtils from '../../utils/fieldCategoryUtils';
import { STAT_FIELDS } from '../../Configurations/StatFieldData';

// Define field label style directly to avoid circular dependency with fieldStyles
const fieldLabelStyle = {
  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
  fontSize: '0.875rem',
  fontWeight: 500,
  letterSpacing: '0.1px'
};

/**
 * Component to display workflow fields organized by categories from the database
 * 
 * @param {Object} props - Component properties
 * @param {Array} props.fields - Array of field definitions 
 * @param {Object} props.formData - Current form data values
 * @param {Function} props.handleChange - Handler for field changes
 * @param {Object} props.validationErrors - Validation error messages by field name
 * @param {Function} props.getFieldType - Function to determine field type
 * @param {Function} props.validateField - Function to validate a field
 * @param {Function} props.handleDateChange - Handler for date field changes
 * @param {Function} props.handleAdditionalInfoChange - Handler for field changes to additionalInfo object
 * @param {Array} props.dbCategories - Field categories from database (optional)
 * @param {Function} props.customFieldRenderer - Custom function to render a field (optional)
 */
const CategorizedFields = ({ 
  fields,
  formData = {},
  validationErrors = {},
  getFieldType,
  validateField = () => true,
  handleDateChange = () => {},
  handleAdditionalInfoChange = () => {},
  dbCategories,
  customFieldRenderer
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);
  const [categorizedFields, setCategorizedFields] = useState({});
  
  // Teal color for all category headers
  const headerColor = '#009688';
  
  // Load field categories from database or use provided categories
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoading(true);
        
        // If dbCategories are provided, use them directly
        if (dbCategories && dbCategories.length > 0) {
          setCategories(dbCategories);
        } else {
          // Otherwise fetch from API
          const fetchedCategories = await fieldCategoryUtils.getFieldCategories();
          setCategories(fetchedCategories);
        }
      } catch (error) {
        console.error('Error loading field categories:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadCategories();
  }, [dbCategories]);
  
  // Organize fields by category when fields or categories change
  useEffect(() => {
    console.log('CategorizedFields: fields changed:', fields);
    console.log('CategorizedFields: categories available:', categories);
    console.log('CategorizedFields: current formData:', formData);
    
    if (!fields || fields.length === 0) {
      console.log('CategorizedFields: No fields to categorize');
      setCategorizedFields({});
      return;
    }
    
    try {
      // Use the utility to group fields by category
      const groupedFields = fieldCategoryUtils.groupFieldsByCategory(fields);
      console.log('CategorizedFields: Fields grouped by category:', groupedFields);
      setCategorizedFields(groupedFields);
    } catch (error) {
      console.error('Error in field categorization:', error);
      
      // On error, create a fallback with all fields in SERVICE_SPECIFIC category
      const fallbackGroupedFields = {
        SERVICE_SPECIFIC: [...fields]
      };
      
      console.log('Using fallback categorization:', fallbackGroupedFields);
      setCategorizedFields(fallbackGroupedFields);
    }
  }, [fields, categories]);
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress size={30} />
      </Box>
    );
  }
  
  // Sort categories by display order if available
  console.log('CategorizedFields before sorting:', categories);
  
  // Handle the possibility that categories is not an array
  let sortedCategories = [];
  if (Array.isArray(categories) && categories.length > 0) {
    sortedCategories = [...categories].sort((a, b) => {
      // Use displayOrder if available, otherwise sort by displayName
      if (a.displayOrder !== undefined && b.displayOrder !== undefined) {
        return a.displayOrder - b.displayOrder;
      }
      return (a.displayName || '').localeCompare(b.displayName || '');
    });
  } else if (!Array.isArray(categories)) {
    console.error('CategorizedFields: categories is not an array:', categories);
    // If categories is an object but not an array, try to convert it
    if (categories && typeof categories === 'object') {
      console.log('CategorizedFields: trying to convert object to array');
      sortedCategories = Object.keys(categories).map(key => ({
        code: key,
        displayName: fieldCategoryUtils.CATEGORY_DISPLAY_NAMES[key] || key
      }));
    }
  }
  
  console.log('CategorizedFields sorted categories:', sortedCategories);
  
  // Add debugging for render phase
  console.log('CategorizedFields RENDER - current state:');
  console.log('  - categorizedFields:', categorizedFields);
  console.log('  - sortedCategories:', sortedCategories);
  
  // Check if we're missing data needed to render
  if (Object.keys(categorizedFields).length === 0) {
    console.log('WARNING: categorizedFields is empty');
    
    // If we have fields but no categorized fields, there might be a problem with categorization
    if (fields && fields.length > 0) {
      console.log('Fields exist but not categorized. Using fallback categorization');
      
      // Create a default category with all fields
      const defaultCategory = {
        code: 'SERVICE_SPECIFIC',
        displayName: 'Service Information'
      };
      
      return (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={defaultCategory.displayName}
                titleTypographyProps={{
                  variant: 'subtitle1',
                  fontWeight: 500,
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  fontSize: '0.875rem',
                  letterSpacing: '0.1px'
                }}
                sx={{
                  backgroundColor: headerColor,
                  color: '#fff',
                  py: 1,
                  paddingLeft: '16px'
                }}
              />
              <CardContent>
                <Grid container spacing={isMobile ? 0 : 2}>
                {fields.map((field, index) => {
                  // Safely get field name with fallback.
                  // Coded dynamic fields are keyed by their title (like null-code fields) so the
                  // ticket shows the readable title rather than the field_xxxx code.
                  const fieldCodeRaw = field.code || '';
                  const fieldName = fieldCodeRaw.startsWith('field_')
                    ? (field.label || field.title || fieldCodeRaw)
                    : (field.name || field.code || field.label || `field_${index}`);
                  const fieldValue = formData[fieldName] || '';
                  const hasError = !!(validationErrors && validationErrors[fieldName]);
                  const errorMessage = validationErrors ? (validationErrors[fieldName] || '') : '';
                  
                  // Map field to the format expected by FieldFactory
                  const fieldConfig = {
                    id: `form-field-${fieldName}-${index}`,
                    name: fieldName,
                    label: field.label || fieldName,
                    required: field.required,
                    description: field.description || '',
                    type: getFieldType ? getFieldType(field) : (field.fieldType || 'text').toLowerCase(),
                    options: field.options || []
                  };
                  
                  // Use customFieldRenderer if provided
                  if (customFieldRenderer) {
                    return customFieldRenderer(field, index);
                  }
                  
                  return (
                    <Grid item xs={12} sm={6} key={`field-${fieldName}-${index}`}>
                      <FieldFactory
                        field={{
                          ...fieldConfig,
                          labelStyle: fieldLabelStyle
                        }}
                        value={fieldConfig.type === 'file' ? (fieldValue || null) : fieldValue}
                        onChange={(e) => {
                          // Special handling for date fields
                          if (fieldConfig.type === 'date' && e.target.value instanceof Date) {
                            handleDateChange(fieldName, e.target.value);
                          } else if (fieldConfig.type === 'file') {
                            // For file fields, we pass the File object directly
                            handleAdditionalInfoChange(fieldName, e, fieldConfig.type.toUpperCase());
                          } else {
                            handleAdditionalInfoChange(fieldName, e.target.value, fieldConfig.type.toUpperCase());
                          }
                        }}
                        onBlur={() => validateField && validateField(fieldName, fieldValue, fieldConfig.type.toUpperCase())}
                        error={hasError}
                        helperText={errorMessage}
                      />
                    </Grid>
                  );
                })}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      );
    }
  }
  
  return (
    <Grid container spacing={3}>
      {sortedCategories.map((category) => {
        // Get the category code from the category object
        const categoryCode = category.code || '';
        
        // Get fields for this category
        const fieldsForCategory = categorizedFields[categoryCode] || [];
        
        // Only render categories that have fields
        if (!fieldsForCategory || fieldsForCategory.length === 0) {
          console.log(`No fields for category ${categoryCode}, skipping render`);
          return null;
        }
        
        console.log(`Rendering category ${categoryCode} with ${fieldsForCategory.length} fields`);
        
        // Get category display name
        const categoryDisplayName = category.displayName || fieldCategoryUtils.CATEGORY_DISPLAY_NAMES[categoryCode] || 'Fields';
        
        return (
          <Grid item xs={12} key={`category-${categoryCode}`}>
            <Card variant="outlined" sx={{ mb: 2 }}>
              <CardHeader
                title={categoryDisplayName}
                titleTypographyProps={{
                  variant: 'subtitle1',
                  fontWeight: 500,
                  fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                  fontSize: '0.875rem',
                  letterSpacing: '0.1px'
                }}
                sx={{
                  backgroundColor: headerColor,
                  color: '#fff',
                  py: 1,
                  paddingLeft: '16px'
                }}
              />
              <CardContent>
                <Grid container spacing={isMobile ? 0 : 2}>
                  {fieldsForCategory.map((field, index) => {
                    // Safely get field name with fallback.
                    // Coded dynamic fields are keyed by their title (like null-code fields) so the
                    // ticket shows the readable title rather than the field_xxxx code.
                    const fieldCode = field.code || '';
                    const isDynamicField = fieldCode.startsWith('field_');
                    const fieldName = isDynamicField ? (field.label || field.title || fieldCode) : (field.name || field.code || field.label || `field_${index}`);
                    
                    // Debug the field value access
                    console.log(`Accessing field ${fieldName} from formData:`, formData);
                    
                    // Get field value with proper fallbacks for different data structures
                    let fieldValue = '';
                    
                    // Direct access - formData might be the additionalInfo object itself
                    // or it might contain an additionalInfo property
                    if (formData && typeof formData === 'object') {
                      if (formData[fieldName] !== undefined) {
                        fieldValue = formData[fieldName];
                        console.log(`Found field ${fieldName} directly in formData:`, fieldValue);
                      } else if (formData.additionalInfo && formData.additionalInfo[fieldName] !== undefined) {
                        fieldValue = formData.additionalInfo[fieldName];
                        console.log(`Found field ${fieldName} in formData.additionalInfo:`, fieldValue);
                      }
                    }
                    
                    console.log(`Final field ${fieldName} value:`, fieldValue);
                    
                    const hasError = !!(validationErrors && validationErrors[fieldName]);
                    const errorMessage = validationErrors ? (validationErrors[fieldName] || '') : '';
                    
                    // Enhanced priority chain for field labels to ensure dynamic fields use title property correctly
                    // This ensures consistent behavior with the FieldText and FileField components
                    const fieldLabel = field.label || field.title || field.name || (STAT_FIELDS[fieldName]?.label || fieldName);
                    
                    // Debug log for field label resolution
                    console.log(`Field ${fieldName} label resolution:`, { 
                      finalLabel: fieldLabel,
                      originalLabel: field.label, 
                      title: field.title,
                      name: field.name,
                      statFieldLabel: STAT_FIELDS[fieldName]?.label
                    });
                    // Map field to the format expected by FieldFactory
                    const fieldConfig = {
                      id: `form-field-${fieldName}-${index}`,
                      name: fieldName,
                      label: fieldLabel,
                      required: field.required,
                      description: field.description || '',
                      type: getFieldType ? getFieldType(field) : (field.fieldType || 'text').toLowerCase(),
                      options: field.options || []
                    };
                    
                    // Use customFieldRenderer if provided
                    if (customFieldRenderer) {
                      return customFieldRenderer(field, index);
                    }
                    
                    return (
                      <Grid item xs={12} sm={6} key={`field-${fieldName}-${index}`}>
                        <FieldFactory
                          field={{
                            ...fieldConfig,
                            labelStyle: fieldLabelStyle
                          }}
                          value={fieldValue}
                          onChange={(e) => {
                            // Special handling for date fields
                            if (fieldConfig.type === 'date' && e.target.value instanceof Date) {
                              handleDateChange(fieldName, e.target.value);
                            } else if (fieldConfig.type === 'file') {
                              // For file fields, we pass the File object directly
                              handleAdditionalInfoChange(fieldName, e, fieldConfig.type.toUpperCase());
                            } else if (typeof e.target.value === 'object' && e.target.value !== null && 
                                      (e.target.value.isReference || e.target.value.hasFile || 
                                       e.target.value.target || e.target.value.documentUrl)) {
                              // For document objects, pass the entire object
                              console.log('Handling document object change:', e.target.value);
                              handleAdditionalInfoChange(fieldName, e.target.value, 'DOCUMENT');
                            } else {
                              handleAdditionalInfoChange(fieldName, e.target.value, fieldConfig.type.toUpperCase());
                            }
                          }}
                          onBlur={() => validateField && validateField(fieldName, fieldValue, fieldConfig.type.toUpperCase())}
                          error={hasError}
                          helperText={errorMessage}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default CategorizedFields;
